import { path } from './client'
import type { ApiClient, Query } from './client'
import type { EnrolmentCode, Locale, Project, ProjectStatus } from './types'

/**
 * Projects, and the enrolment codes that let people into them.
 *
 * The codes live here rather than in a module of their own because a code has
 * no meaning apart from its project: it is minted per cohort or per village so
 * that "where did these members come from" has an answer, and every path it
 * appears on is nested under the project it belongs to.
 *
 * Two shapes here were wrong against the server and neither failed loudly,
 * which is the only reason they survived to be found by somebody opening a
 * screen.
 *
 * The write bodies named fields the server has never had. A project has an
 * `objective`, not a `summary`, and it has no start or end date at all, and
 * unrecognised keys are dropped rather than refused, so creating a project
 * answered 201 while throwing away the sentence and the two dates somebody had
 * just typed. The fields below are now exactly the ones `POST` and `PATCH`
 * accept.
 *
 * The codes list is a page, not an array. Reading `.length` off it gave
 * `undefined` on the projects table and handing it to a table component as rows
 * took the codes screen down entirely.
 */

export interface ProjectListQuery extends Query {
  status?: ProjectStatus
  limit?: number
  cursor?: string
}

export interface EnrolmentCodeListQuery extends Query {
  limit?: number
  cursor?: string
}

/**
 * What a project can be created or edited with.
 *
 * `escalationRules` is deliberately absent even though the project responds
 * with it. The rules are phrase sets per locale consumed by the escalation
 * path, the server does not accept them on this route, and a console that
 * offered to edit them would be offering a control that silently does nothing.
 */
export interface ProjectInput {
  name: string
  /** What the project is trying to change. Null clears it. */
  objective?: string | null
  /** What a session opens in when it does not say. Must be one of `locales`. */
  defaultLocale?: Locale
  locales?: Locale[]
  /** Phrases Kura refuses here whatever the knowledge says. Sent whole, not patched. */
  mustNotSay?: string[]
}

export interface EnrolmentCodeInput {
  /** What this cohort is called. Shown to staff, never to the person enrolling. */
  label: string
  /** Null for unlimited, which is the right default for a radio announcement. */
  maxUses?: number | null
  expiresAt?: string | null
  /** Omitted means the server mints a sayable one. Supplying one is for reprints. */
  code?: string
}

export function projectsApi(http: ApiClient) {
  return {
    /**
     * One page of projects.
     *
     * `status` is the only filter, because it is the only one the server reads.
     * A `q` was sent here and quietly ignored, so the search box returned every
     * project and presented that as the result of searching. A filter that
     * silently does nothing is worse than no filter, because the rows on screen
     * look like matches.
     */
    list(query: ProjectListQuery = {}) {
      return http.list<Project>('/v1/org/projects', query)
    },

    get(id: string) {
      return http.request<Project>(path`/v1/org/projects/${id}`)
    },

    create(input: ProjectInput) {
      return http.request<Project>('/v1/org/projects', { method: 'POST', body: input })
    },

    update(id: string, input: Partial<ProjectInput> & { status?: ProjectStatus }) {
      return http.request<Project>(path`/v1/org/projects/${id}`, { method: 'PATCH', body: input })
    },

    /**
     * Closes a project. There is no delete.
     *
     * A closed project stops enrolling, stops broadcasting and stops feeding
     * Kura, and everything it has already produced stays readable. Deleting it
     * would take a year of indicator periods with it, and those are what a
     * donor report is made of.
     *
     * This was `DELETE /v1/org/projects/:id`, which is a real route that really
     * deletes. The server refuses when the project has ever had members, so the
     * damage was bounded to projects with no history, and inside that bound it
     * was total: the dialog promising that nothing is deleted destroyed the row,
     * and answered 204 while the console was typed as though a project had come
     * back. A status change is what closing has always meant.
     *
     * The reason somebody types is not sent, because there is nowhere to put
     * it: a project carries no close reason and no close date, only a status.
     * Collecting one and discarding it is the failure this whole module exists
     * to stop repeating, so the dialog no longer asks. A `closeReason` on the
     * project is the fix, and it is a server change.
     */
    close(id: string) {
      return http.request<Project>(path`/v1/org/projects/${id}`, {
        method: 'PATCH',
        body: { status: 'closed' },
      })
    },

    codes: {
      list(projectId: string, query: EnrolmentCodeListQuery = {}) {
        return http.list<EnrolmentCode>(path`/v1/org/projects/${projectId}/codes`, query)
      },

      create(projectId: string, input: EnrolmentCodeInput) {
        return http.request<EnrolmentCode>(path`/v1/org/projects/${projectId}/codes`, {
          method: 'POST',
          body: input,
        })
      },

      /**
       * Turns a code off without removing it.
       *
       * The row is what ties every member who used it to the cohort they came
       * from, so deactivating and deleting are not interchangeable. Codes are
       * guessable by design, which is what makes them sayable, so the server
       * rate limits them hard and audits every successful use.
       */
      setActive(projectId: string, codeId: string, active: boolean) {
        return http.request<EnrolmentCode>(path`/v1/org/projects/${projectId}/codes/${codeId}`, {
          method: 'PATCH',
          body: { active },
        })
      },
    },
  }
}

export type ProjectsApi = ReturnType<typeof projectsApi>
